import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import DeleteIcon from '@mui/icons-material/Delete'
import {
	Autocomplete,
	Box,
	Button,
	Container,
	Divider,
	IconButton,
	Stack,
	TextField,
	Typography,
} from '@mui/material'
import { ToastSeverity } from 'constants/enums/ToastSeverityEnum'
import { ADMIN_PAGE } from 'constants/routes'

import { useToastContext } from 'contexts/ToastProvider'

import { addNewUser, getAllUsers } from 'services/admin.service'

import BreadCrumbs from 'components/BreadCrumbs'
import SubPageHeader from 'components/SubPageHeader'

import { styles } from './AddNewUsers.styles'

export const TEST_ID = 'add-new-users-page'

const MAX_USERS = 10

const EMAIL_REGEX =
	/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i

const parseUniqueEmails = (entries: string[]): string[] => {
	const splitRegex = /[,\n\s]/
	const uniqueEmails = new Set<string>()
	entries.forEach((entry) => {
		entry
			.split(splitRegex)
			.map((e) => e.trim())
			.filter((e) => e && EMAIL_REGEX.test(e))
			.forEach((e) => uniqueEmails.add(e))
	})
	return [...uniqueEmails]
}

interface PendingUser {
	email: string
	role: 'Facilitator'
}

const AddNewUsersPage = () => {
	const navigate = useNavigate()
	const { showAlert } = useToastContext()

	const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([])
	const [chipEmails, setChipEmails] = useState<string[]>([])
	const [chipInputValue, setChipInputValue] = useState('')
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [isLoadingUsers, setIsLoadingUsers] = useState(true)
	const [existingUserEmails, setExistingUserEmails] = useState<Set<string>>(
		new Set()
	)

	useEffect(() => {
		const fetchAllUsers = async () => {
			const allUsers: { email: string }[] = []
			let page = 1
			const pageSize = 100
			let totalCount = Infinity

			while (allUsers.length < totalCount) {
				const response = await getAllUsers({ page, pageSize })
				const data = response.data
				totalCount = data.metadata.totalCount

				if (data.records.length === 0) break

				allUsers.push(...data.records)
				page++
			}

			return allUsers
		}

		fetchAllUsers()
			.then((users) => {
				setExistingUserEmails(
					new Set(users.map((u) => u.email.toLowerCase()))
				)
			})
			.catch(() => {
				showAlert?.({
					severity: ToastSeverity.ERROR,
					title: 'Error Loading Users',
					bodyText: 'Could not load existing users.',
				})
			})
			.finally(() => {
				setIsLoadingUsers(false)
			})
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	// Auth guard: redirect to /admin if the admin page was never unlocked
	if (sessionStorage.getItem('isAdminPageAuthorized') !== 'true') {
		navigate(ADMIN_PAGE)
		return null
	}

	const handleChipsChange = (
		_event: React.ChangeEvent<unknown>,
		value: string[]
	) => {
		setChipEmails(parseUniqueEmails(value))
		setChipInputValue('')
	}

	const handleAdd = () => {
		const validCandidates: string[] = []

		for (const email of chipEmails) {
			const normalised = email.toLowerCase()
			if (/@slalom\.com$/i.test(email)) {
				showAlert?.({
					severity: ToastSeverity.ERROR,
					title: 'Cannot Add User',
					bodyText: `${email} is already a facilitator`,
				})
				continue
			}
			if (existingUserEmails.has(normalised)) {
				showAlert?.({
					severity: ToastSeverity.ERROR,
					title: 'Cannot Add User',
					bodyText: `${email} already exists as a user`,
				})
				continue
			}
			validCandidates.push(email)
		}

		setPendingUsers((prev) => {
			let next = [...prev]
			for (const email of validCandidates) {
				const normalised = email.toLowerCase()
				if (next.some((u) => u.email.toLowerCase() === normalised)) {
					continue
				}
				if (next.length >= MAX_USERS) {
					continue
				}
				next = [...next, { email, role: 'Facilitator' }]
			}
			return next
		})

		setChipEmails([])
		setChipInputValue('')
	}

	const handleRemove = (email: string) => {
		setPendingUsers((prev) => prev.filter((u) => u.email !== email))
	}

	const handleSubmit = async () => {
		setIsSubmitting(true)
		try {
			const results = await Promise.allSettled(
				pendingUsers.map((user) =>
					addNewUser({ email: user.email, role: user.role })
				)
			)
			const failures = results.filter((r) => r.status === 'rejected')
			if (failures.length > 0) {
				showAlert?.({
					severity: ToastSeverity.ERROR,
					title: 'Error Adding Users',
					bodyText: `${failures.length} of ${pendingUsers.length} users could not be added. Please try again.`,
				})
			}
			if (failures.length < pendingUsers.length) {
				navigate(ADMIN_PAGE)
			}
		} catch {
			showAlert?.({
				severity: ToastSeverity.ERROR,
				title: 'Error Adding Users',
				bodyText:
					'There was an error adding one or more users. Please try again.',
			})
		} finally {
			setIsSubmitting(false)
		}
	}

	const handleBack = () => navigate(ADMIN_PAGE)
	const handleCancel = () => navigate(ADMIN_PAGE)

	const isAddDisabled =
		isLoadingUsers ||
		chipEmails.length === 0 ||
		pendingUsers.length + chipEmails.length > MAX_USERS

	return (
		<Stack data-testid={TEST_ID} sx={styles.stackContainer}>
			<SubPageHeader
				title="User Management"
				subtitle="Manage Brainstorm teams and users here"
				backgroundGradient="linear-gradient(90deg, #9DCDF2 0%, #BDDEF6 100%)"
			/>

			<Box sx={styles.breadcrumbContainer}>
				<BreadCrumbs
					breadcrumbs={[
						{
							label: 'User Management',
							onClick: () => navigate(ADMIN_PAGE),
						},
						{
							label: 'Add New Users',
							active: true,
						},
					]}
				/>
			</Box>
			<Box sx={styles.contentBox}>
				<Container maxWidth="md" sx={styles.containerInner}>
					<Box sx={styles.scrollableContent}>
						<Typography
							variant="h5"
							sx={styles.pageTitle}
							data-testid={`${TEST_ID}__title`}
						>
							Add New Users
						</Typography>

						<Divider sx={styles.divider} />

						<Typography sx={styles.formLabel}>
							Enter email(s) to add up to 10 new users at a time
						</Typography>

						<Box sx={styles.emailRow}>
							<Box
								data-testid={`${TEST_ID}__email-input`}
								sx={styles.autocompleteContainer}
							>
								<Autocomplete
									multiple
									options={[]}
									value={chipEmails}
									inputValue={chipInputValue}
									freeSolo
									fullWidth
									ChipProps={{ color: 'secondary' }}
									renderInput={(params) => (
										<TextField
											{...params}
											placeholder={
												chipEmails.length
													? ''
													: 'Add email'
											}
											variant="outlined"
										/>
									)}
									onChange={handleChipsChange}
									onInputChange={(event, newInputEmail) => {
										const parts =
											newInputEmail.split(/([ ,\n])/)
										if (parts.length > 1) {
											handleChipsChange(event, [
												...chipEmails,
												...parts,
											])
										} else {
											setChipInputValue(newInputEmail)
										}
									}}
								/>
							</Box>
							<Button
								data-testid={`${TEST_ID}__add-button`}
								variant="contained"
								color="secondary"
								disabled={isAddDisabled}
								onClick={handleAdd}
								sx={styles.addButton}
							>
								ADD
							</Button>
						</Box>

						{pendingUsers.length > 0 && (
							<Typography
								sx={styles.usersAddedLabel}
								data-testid={`${TEST_ID}__users-added-label`}
							>
								{`Users added (${pendingUsers.length})`}
							</Typography>
						)}

						{pendingUsers.length > 0 && (
							<Box sx={styles.userListContainer}>
								{pendingUsers.map((user, index) => (
									<React.Fragment key={user.email}>
										{index > 0 && <Divider />}
										<Box
											sx={styles.userRow}
											data-testid={`${TEST_ID}__user-row`}
										>
											<Typography
												sx={styles.userEmail}
												data-testid={`${TEST_ID}__user-email-${user.email}`}
											>
												{user.email}
											</Typography>
											<Typography
												sx={styles.userRole}
												data-testid={`${TEST_ID}__user-role-${user.email}`}
											>
												{user.role}
											</Typography>
											<IconButton
												data-testid={`${TEST_ID}__remove-${user.email}`}
												size="small"
												onClick={() =>
													handleRemove(user.email)
												}
												aria-label={`Remove ${user.email}`}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</Box>
									</React.Fragment>
								))}
							</Box>
						)}
					</Box>

					<Box sx={styles.actionRow}>
						<Typography
							variant="body2"
							sx={styles.infoText}
							data-testid={`${TEST_ID}__info-text`}
						>
							When the submit button is pressed, Brainstorm will
							send the user(s) an automated email invitation and
							add them to our approved user pool on the backend.
						</Typography>
						<Box sx={styles.buttonsRow}>
							<Button
								data-testid={`${TEST_ID}__back-button`}
								onClick={handleBack}
								sx={styles.secondaryButton}
							>
								BACK
							</Button>
							<Box sx={styles.rightButtons}>
								<Button
									data-testid={`${TEST_ID}__cancel-button`}
									onClick={handleCancel}
									sx={styles.secondaryButton}
								>
									CANCEL
								</Button>
								<Button
									data-testid={`${TEST_ID}__submit-button`}
									variant="contained"
									color="secondary"
									disabled={
										isSubmitting ||
										pendingUsers.length === 0
									}
									onClick={handleSubmit}
								>
									SUBMIT
								</Button>
							</Box>
						</Box>
					</Box>
				</Container>
			</Box>
		</Stack>
	)
}

export default AddNewUsersPage
